/* Optional theme-only presentation for the public Bazaarvoice reviews shadow root.
 * Native rendering, data, events and commerce remain owned by the app.
 */
(() => {
  'use strict';
  if (!window.customElements || customElements.get('v6-review-frame')) return;

  class V6ReviewFrame extends HTMLElement {
    connectedCallback() {
      this.disconnectWatchers();
      this.applied = false;
      this.timers = [];
      if (this.applyStyles()) return;

      // Reviews can be lazy-loaded far below the purchase area. Begin the bounded
      // readiness window when this frame approaches the viewport, not at page load.
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.watchForApp();
        }, { rootMargin: '800px' });
        this.visibilityObserver.observe(this);
      } else {
        this.watchForApp();
      }
    }

    disconnectedCallback() {
      this.disconnectWatchers();
    }

    disconnectWatchers() {
      this.visibilityObserver?.disconnect();
      this.appObserver?.disconnect();
      (this.timers || []).forEach(clearTimeout);
      this.timers = [];
    }

    watchForApp() {
      this.visibilityObserver?.disconnect();
      if (!this.isConnected || this.applyStyles()) return;

      this.appObserver = new MutationObserver(() => this.applyStyles());
      this.appObserver.observe(this, { childList: true, subtree: true });

      // Attaching a shadow root does not emit a light-DOM mutation. Sparse,
      // frame-local retries cover that race and always end after 30 seconds.
      [100, 300, 800, 1600, 3200, 6400, 12000, 20000, 30000].forEach((delay) => {
        this.timers.push(setTimeout(() => {
          if (this.isConnected) this.applyStyles();
          if (delay === 30000 || !this.isConnected) this.disconnectWatchers();
        }, delay));
      });
    }

    applyStyles() {
      if (this.applied) return true;
      const host = this.querySelector('[data-bv-show="reviews"]');
      const root = host?.shadowRoot;
      const stylesheet = this.dataset.reviewStylesheet;
      if (!root || root.mode !== 'open' || !stylesheet) return false;

      // Access only the normal, public open root. Closed/missing roots retain the
      // vendor's default appearance; no attachShadow patch or vendor API hooks.
      if (!root.querySelector('link[data-v6-review-styles]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = stylesheet;
        link.setAttribute('data-v6-review-styles', '');
        root.appendChild(link);
      }
      this.applied = true;
      this.disconnectWatchers();
      return true;
    }
  }

  // Custom-element lifecycle also handles Shopify section load/unload in the editor.
  customElements.define('v6-review-frame', V6ReviewFrame);
})();
